import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDisclosure } from '@withjoy/joykit';
import { addError } from '@shared/utils/logger';
import { EventPersonContextProvider } from './EventPerson.context';
import { EventPersonNameDialog } from './EventPersonNameDialog.internal';
import { useActiveEventPersonQuery, useCreateEventPersonSessionMutation } from './EventPerson.graphql.generated';
import type { EventPerson, EventPersonContextValue } from './EventPerson.types';

type EventPersonProviderProps = Readonly<{
  eventId: string;
}>;

/**
 * Guest-wide holder of the current event_person identity. Loads `activeEventPerson`, owns the
 * display-name dialog, and exposes caller-driven `requestIdentity()`. On submit it creates the session then refetches.
 */
export const EventPersonProvider: React.FC<EventPersonProviderProps> = ({ eventId, children }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  // Resolver for the promise returned by the in-flight `requestIdentity()` call.
  const pendingResolveRef = useRef<((person: EventPerson | null) => void) | null>(null);
  // True while a `requestIdentity()` call is parked waiting for the initial
  // `activeEventPerson` query to settle before deciding whether to prompt.
  const [awaitingLoad, setAwaitingLoad] = useState(false);

  const { data, loading, refetch } = useActiveEventPersonQuery({
    variables: { eventId },
    skip: !eventId,
    ssr: false,
    batchMode: 'fast',
  });
  const [createSession, { loading: isSubmitting }] = useCreateEventPersonSessionMutation();

  const eventPerson = data?.activeEventPerson ?? null;

  const settlePending = useCallback((person: EventPerson | null) => {
    const resolve = pendingResolveRef.current;
    pendingResolveRef.current = null;
    resolve?.(person);
  }, []);

  const requestIdentity = useCallback(() => {
    if (eventPerson) {
      return Promise.resolve<EventPerson | null>(eventPerson);
    }
    return new Promise<EventPerson | null>(resolve => {
      // Settle any prior pending request as dismissed first so we never leak an unresolved promise.
      pendingResolveRef.current?.(null);
      pendingResolveRef.current = resolve;
      // A returning guest may have a server-side session that's still loading. Don't
      // prompt yet (a re-entered name would create a duplicate event_person) — park the
      // request and let the effect below decide once the query settles.
      if (loading) {
        setAwaitingLoad(true);
      } else {
        onOpen();
      }
    });
  }, [eventPerson, loading, onOpen]);

  // Resolve a parked request once the identity query settles: hand back the loaded
  // person if one now exists (no prompt), otherwise open the name dialog.
  useEffect(() => {
    if (!awaitingLoad || loading) return;
    setAwaitingLoad(false);
    if (eventPerson) {
      settlePending(eventPerson);
    } else {
      onOpen();
    }
  }, [awaitingLoad, loading, eventPerson, settlePending, onOpen]);

  const handleClose = useCallback(() => {
    onClose();
    settlePending(null);
  }, [onClose, settlePending]);

  const handleSubmit = useCallback(
    async (displayName: string) => {
      try {
        const result = await createSession({ variables: { eventId, displayName } });
        const person = result.data?.createEventPersonSession ?? null;
        // Refetch so `activeEventPerson` (a root-query field Apollo can't normalize from the mutation) reflects the new identity.
        await refetch();
        onClose();
        settlePending(person);
      } catch (error) {
        // Keep the dialog open so the guest can retry.
        addError(error instanceof Error ? error : new Error('createEventPersonSession failed'), { context: 'EventPersonProvider.createSession' });
      }
    },
    [createSession, eventId, refetch, onClose, settlePending],
  );

  const value = useMemo<EventPersonContextValue>(() => ({ eventPerson, loading, requestIdentity }), [eventPerson, loading, requestIdentity]);

  return (
    <EventPersonContextProvider value={value}>
      {children}
      <EventPersonNameDialog
        isOpen={isOpen}
        isSubmitting={isSubmitting}
        onClose={handleClose}
        onSubmit={handleSubmit}
      />
    </EventPersonContextProvider>
  );
};
